import type { SoldItem, SoldLines, SoldService } from '../types/agreements.types';
import { availableItemUnits, isPartlyInvoiced, selectionKeySet, unitKey } from './invoiceSelection';
import type { SelectionUnit } from './invoiceSelection';
import { addOnLineScaled, fromScaled, itemLineScaled, toScaled } from './soldLineAmounts';

export interface SelectionSummaryLine {
    key: string;
    label: string;
    serviceName: string | null;
    net: number;
    gross: number;
    rowKeys: string[];
}

const itemKey = (service: SoldService, item: SoldItem): string =>
    unitKey({ kind: 'item', serviceId: service.id, itemId: item.id });

function collapsedPartlyInvoiced(service: SoldService, selectedKeys: ReadonlySet<string>): SelectionSummaryLine | null {
    if (!isPartlyInvoiced(service)) return null;

    const units = availableItemUnits(service);
    if (!units.every((unit) => selectedKeys.has(unitKey(unit)))) return null;

    const available = service.items.filter((item) => item.status === 'available');
    const netScaled = available.reduce((sum, item) => sum + itemLineScaled(item).net, 0);
    const grossScaled = available.reduce((sum, item) => sum + itemLineScaled(item).gross, 0);
    return {
        key: unitKey({ kind: 'service', serviceId: service.id }),
        label: service.name,
        serviceName: null,
        net: fromScaled(netScaled),
        gross: fromScaled(grossScaled),
        rowKeys: units.map(unitKey),
    };
}

function serviceLines(service: SoldService, selectedKeys: ReadonlySet<string>): SelectionSummaryLine[] {
    const serviceKey = unitKey({ kind: 'service', serviceId: service.id });
    if (selectedKeys.has(serviceKey)) {
        return [{ key: serviceKey, label: service.name, serviceName: null, net: fromScaled(toScaled(service.net)), gross: fromScaled(toScaled(service.gross)), rowKeys: [serviceKey] }];
    }

    const collapsed = collapsedPartlyInvoiced(service, selectedKeys);
    if (collapsed) return [collapsed];

    return service.items
        .filter((item) => selectedKeys.has(itemKey(service, item)))
        .map((item) => ({
            key: itemKey(service, item),
            label: item.name,
            serviceName: service.name,
            net: fromScaled(itemLineScaled(item).net),
            gross: fromScaled(itemLineScaled(item).gross),
            rowKeys: [itemKey(service, item)],
        }));
}

export function selectionSummaryLines(selection: SelectionUnit[], lines: SoldLines): SelectionSummaryLine[] {
    const selectedKeys = selectionKeySet(selection);
    const addOnLines = lines.addOns.flatMap((addOn): SelectionSummaryLine[] => {
        const key = unitKey({ kind: 'add_on', addOnId: addOn.id });
        if (!selectedKeys.has(key)) return [];
        return [{ key, label: addOn.name, serviceName: null, net: fromScaled(addOnLineScaled(addOn).net), gross: fromScaled(addOnLineScaled(addOn).gross), rowKeys: [key] }];
    });

    return [...lines.services.flatMap((service) => serviceLines(service, selectedKeys)), ...addOnLines];
}
